import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState, type ComponentType } from 'react';
import { createPortal } from 'react-dom';
import { HexColorPicker } from 'react-colorful';
import { PageTitle } from '@/components/ui/PageTitle';
import { Button, SlidingTabs } from '@/components/ui';
import { DataTablePagination } from '@/components/data-table/Pagination';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { useGetForm, useGetFormAnswers } from '@/services/queries/form';
import { useGetFormCategories } from '@/services/queries/form-category';
import { useGetCompanies } from '@/services/queries/company';
import {
	useCreateFormQuestion,
	usePatchFormQuestion,
	useRemoveFormQuestion,
	useReorderFormQuestions,
	useAddFormAudience,
	useRemoveFormAudience,
} from '@/services/mutations/form';
import {
	resolveColorInputToHex,
	getContrastTextClassForHex,
} from '@/utils/formQuestionColor';
import { downloadCsv } from '@/utils/downloadCsv';
import { cn } from '@/lib/utils';
import { MATCH_QUESTION_CODE_LABELS } from '@/constants/professionalTags';
import {
	Plus,
	Trash2,
	ArrowLeft,
	Pencil,
	X,
	MessageSquare,
	Building2,
	AlertTriangle,
	Loader2,
	Search,
	ChevronUp,
	ChevronDown,
	FoldVertical,
	UnfoldVertical,
	ListChecks,
	Type,
	Download,
} from 'lucide-react';
import {
	DynamicIcon as DynamicIconRaw,
	iconNames,
	type IconName,
} from 'lucide-react/dynamic';

const DynamicIcon = DynamicIconRaw as ComponentType<{
	name: IconName;
	size?: number;
	className?: string;
}>;

const QUICK_COLORS: string[] = [
	// Linha 1 - arco-íris e complementares
	'#ef4444', // vermelho
	'#f97316', // laranja
	'#facc15', // amarelo
	'#84cc16', // lima
	'#22c55e', // verde
	'#22d3ee', // ciano
	'#60a5fa', // azul claro
	'#ec4899', // magenta

	// Linha 2 - variações/cores secundárias
	'#b91c1c', // vermelho escuro
	'#c2410c', // laranja queimado
	'#ca8a04', // amarelo oliva
	'#166534', // verde escuro
	'#047857', // esmeralda
	'#0e7490', // teal escuro
	'#1d4ed8', // azul profundo
	'#6d28d9', // roxo intenso

	// Linha 3 - especiais e neutros
	'#fee2e2', // vermelho pastel
	'#ffedd5', // pêssego pastel
	'#fef9c3', // amarelo pastel
	'#dcfce7', // verde pastel
	'#e0f2fe', // azul pastel
	'#f9fafb', // quase branco
	'#d4d4d8', // cinza médio
	'#111827', // quase preto
];

const ColorPopover = ({
	value,
	onChange,
}: {
	value: string;
	onChange: (v: string) => void;
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const popoverRef = useRef<HTMLDivElement | null>(null);
	const [position, setPosition] = useState<{
		top: number;
		left: number;
	} | null>(null);

	const pickerColor = resolveColorInputToHex(value.trim()) ?? '#e5e7eb';
	const swatchColor = resolveColorInputToHex(value.trim()) ?? '#e5e7eb';

	const open = () => {
		if (!triggerRef.current) return;
		const rect = triggerRef.current.getBoundingClientRect();
		const width = 230;
		const estimatedHeight = 320;
		const offset = 8;
		let left = rect.left + rect.width / 2 - width / 2;
		left = Math.max(16, Math.min(left, window.innerWidth - width - 16));

		let top: number;
		if (rect.bottom + offset + estimatedHeight > window.innerHeight) {
			top = rect.top - offset - estimatedHeight + window.scrollY;
		} else {
			top = rect.bottom + offset + window.scrollY;
		}
		setPosition({ top, left });
		setIsOpen(true);
	};

	useEffect(() => {
		if (!isOpen) return;
		const handleClick = (event: MouseEvent) => {
			const target = event.target as Node;
			if (
				popoverRef.current &&
				!popoverRef.current.contains(target) &&
				triggerRef.current &&
				!triggerRef.current.contains(target)
			) {
				setIsOpen(false);
			}
		};
		const handleKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setIsOpen(false);
		};
		document.addEventListener('mousedown', handleClick);
		document.addEventListener('keydown', handleKey);
		return () => {
			document.removeEventListener('mousedown', handleClick);
			document.removeEventListener('keydown', handleKey);
		};
	}, [isOpen]);

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				onClick={() => (isOpen ? setIsOpen(false) : open())}
				className="flex h-8 w-8 items-center justify-center rounded-full border border-stone-200 bg-white"
			>
				<span
					className="h-6 w-6 rounded-full"
					style={{ backgroundColor: swatchColor }}
				/>
			</button>
			{isOpen &&
				position &&
				createPortal(
					<div
						ref={popoverRef}
						className="z-[9999] rounded-2xl border border-stone-100 bg-white p-3 shadow-xl"
						style={{
							position: 'absolute',
							top: position.top,
							left: position.left,
							width: 230,
						}}
					>
						<div className="flex flex-col items-center gap-2">
							<div className="w-full">
								<HexColorPicker
									color={pickerColor}
									onChange={onChange}
								/>
							</div>
							<div className="grid w-full grid-cols-8 gap-1 pt-2">
								{QUICK_COLORS.map((c) => (
									<button
										key={c}
										type="button"
										onClick={() => onChange(c)}
										className="h-6 w-6 rounded-full border border-stone-200"
										style={{ backgroundColor: c }}
									/>
								))}
							</div>
						</div>
					</div>,
					document.body,
				)}
		</>
	);
};

const ICON_POPOVER_WIDTH = 320;
const ICON_POPOVER_MAX_HEIGHT = 360;
const ICON_GRID_LIMIT = 120;

const LucideIconPopover = ({
	value,
	onChange,
}: {
	value: string;
	onChange: (name: string) => void;
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const popoverRef = useRef<HTMLDivElement | null>(null);
	const [position, setPosition] = useState<{
		top: number;
		left: number;
	} | null>(null);

	const open = () => {
		if (!triggerRef.current) return;
		const rect = triggerRef.current.getBoundingClientRect();
		const width = ICON_POPOVER_WIDTH;
		let left = rect.left + rect.width / 2 - width / 2;
		left = Math.max(16, Math.min(left, window.innerWidth - width - 16));
		const viewportHeight = window.innerHeight;
		const offset = 8;
		const estimatedHeight = ICON_POPOVER_MAX_HEIGHT;

		let top: number;
		if (rect.bottom + offset + estimatedHeight > viewportHeight) {
			// Abre para cima se não couber para baixo
			top = rect.top - offset - estimatedHeight + window.scrollY;
		} else {
			// Padrão: abre para baixo
			top = rect.bottom + offset + window.scrollY;
		}

		setPosition({ top, left });
		setSearchQuery('');
		setIsOpen(true);
	};

	useEffect(() => {
		if (!isOpen) return;
		const handleClick = (event: MouseEvent) => {
			const target = event.target as Node;
			if (
				popoverRef.current &&
				!popoverRef.current.contains(target) &&
				triggerRef.current &&
				!triggerRef.current.contains(target)
			) {
				setIsOpen(false);
			}
		};
		const handleKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setIsOpen(false);
		};
		document.addEventListener('mousedown', handleClick);
		document.addEventListener('keydown', handleKey);
		return () => {
			document.removeEventListener('mousedown', handleClick);
			document.removeEventListener('keydown', handleKey);
		};
	}, [isOpen]);

	const filteredNames = (iconNames as IconName[]).filter((name) =>
		name.toLowerCase().includes(searchQuery.toLowerCase().trim()),
	);
	const displayNames = filteredNames.slice(0, ICON_GRID_LIMIT);

	return (
		<>
			<div className="flex items-center gap-1">
				<button
					ref={triggerRef}
					type="button"
					onClick={() => (isOpen ? setIsOpen(false) : open())}
					className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-500 transition-colors hover:bg-stone-50"
					title="Escolher ícone"
				>
					{value ? (
						<DynamicIcon
							name={value as IconName}
							size={18}
							className="text-stone-700"
						/>
					) : (
						<span className="text-base opacity-60">😀</span>
					)}
				</button>
				{value && (
					<button
						type="button"
						onClick={() => onChange('')}
						className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
						title="Remover ícone"
					>
						<X size={16} />
					</button>
				)}
			</div>
			{isOpen &&
				position &&
				createPortal(
					<div
						ref={popoverRef}
						className="z-[9999] flex flex-col overflow-hidden rounded-2xl border border-stone-100 bg-white shadow-xl"
						style={{
							position: 'absolute',
							top: position.top,
							left: position.left,
							width: ICON_POPOVER_WIDTH,
							maxHeight: ICON_POPOVER_MAX_HEIGHT,
						}}
					>
						<div className="flex items-center gap-2 border-b border-stone-100 px-3 py-2">
							<Search className="h-4 w-4 shrink-0 text-stone-400" />
							<input
								type="text"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								placeholder="Buscar ícone..."
								className="flex-1 bg-transparent text-sm text-stone-800 outline-none placeholder:text-stone-400"
							/>
						</div>
						<div className="flex-1 overflow-y-auto p-2">
							<div className="grid grid-cols-8 gap-1">
								{displayNames.map((name) => (
									<button
										key={name}
										type="button"
										onClick={() => {
											onChange(name);
											setIsOpen(false);
										}}
										className="flex h-9 w-9 items-center justify-center rounded-lg text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-800"
										title={name}
									>
										<DynamicIcon name={name} size={20} />
									</button>
								))}
							</div>
							{filteredNames.length > ICON_GRID_LIMIT && (
								<p className="mt-2 text-center text-xs text-stone-400">
									Mostrando {ICON_GRID_LIMIT} de{' '}
									{filteredNames.length}. Refine a busca.
								</p>
							)}
						</div>
					</div>,
					document.body,
				)}
		</>
	);
};

const QuestionColorPicker = ({
	label,
	value,
	onChange,
	iconValue,
	iconOnChange,
	labelClass,
	inputClass,
}: {
	label: string;
	value: string;
	onChange: (v: string) => void;
	iconValue?: string;
	iconOnChange?: (name: string) => void;
	labelClass: string;
	inputClass: string;
}) => {
	return (
		<div className="space-y-3">
			<div className="space-y-2">
				<label className={labelClass}>{label}</label>
				<div className="flex items-center gap-3">
					{iconOnChange != null && (
						<LucideIconPopover
							value={iconValue ?? ''}
							onChange={iconOnChange}
						/>
					)}
					<input
						type="text"
						value={value}
						onChange={(e) => onChange(e.target.value)}
						placeholder="Ex: #0f766e ou bg-teal-500"
						className={`${inputClass} flex-1`}
					/>
					<ColorPopover value={value} onChange={onChange} />
				</div>
			</div>
		</div>
	);
};

const OptionScoreInput = ({
	value,
	onChange,
}: {
	value?: number;
	onChange: (value: number | undefined) => void;
}) => {
	const handleInputChange = (rawValue: string) => {
		if (rawValue.trim() === '') {
			onChange(undefined);
			return;
		}
		const numeric = Number(rawValue);
		if (!Number.isFinite(numeric)) return;
		onChange(Math.max(0, Math.trunc(numeric)));
	};

	const increment = () => {
		onChange((value ?? 0) + 1);
	};

	const decrement = () => {
		if (value === undefined) return;
		onChange(Math.max(0, value - 1));
	};

	return (
		<div className="flex h-9 w-[112px] overflow-hidden rounded-lg border border-stone-200 bg-white">
			<input
				type="text"
				inputMode="numeric"
				value={value ?? ''}
				onChange={(e) => handleInputChange(e.target.value)}
				placeholder="Score"
				className="min-w-0 flex-1 border-0 bg-transparent px-2 text-sm outline-none"
			/>
			<div className="flex w-7 shrink-0 flex-col border-l border-stone-200">
				<button
					type="button"
					onClick={increment}
					className="flex h-1/2 items-center justify-center border-b border-stone-200 text-stone-500 transition-colors hover:bg-stone-50 hover:text-stone-700"
					aria-label="Aumentar score"
				>
					<ChevronUp size={12} />
				</button>
				<button
					type="button"
					onClick={decrement}
					className="flex h-1/2 items-center justify-center text-stone-500 transition-colors hover:bg-stone-50 hover:text-stone-700"
					aria-label="Diminuir score"
				>
					<ChevronDown size={12} />
				</button>
			</div>
		</div>
	);
};

const QUESTION_TYPE_OPTIONS: Array<{
	value: Forms.FormQuestionType;
	label: string;
}> = [
	{ value: 'SINGLE_CHOICE', label: 'Escolha única' },
	{ value: 'MULTIPLE_CHOICE', label: 'Múltipla escolha' },
	{ value: 'FREE_TEXT', label: 'Texto livre' },
];

const NO_CATEGORY_VALUE = '__NO_CATEGORY__';
const NO_SUBCATEGORY_VALUE = '__NO_SUBCATEGORY__';

const getDefaultOptions = (): Array<{
	key: string;
	label: string;
	score?: number;
	color?: string;
	icon_identifier?: string;
}> => [
	{
		key: 'A',
		label: '',
		score: undefined,
		color: undefined,
		icon_identifier: undefined,
	},
	{
		key: 'B',
		label: '',
		score: undefined,
		color: undefined,
		icon_identifier: undefined,
	},
];

const questionTypeLabel = (type: Forms.FormQuestionType) =>
	QUESTION_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;

const questionTypeIcon = (type: Forms.FormQuestionType) => {
	if (type === 'SINGLE_CHOICE') {
		return (
			<span className="text-[10px] font-black leading-none text-stone-600">
				1
			</span>
		);
	}
	if (type === 'MULTIPLE_CHOICE') return <ListChecks size={12} />;
	return <Type size={12} />;
};

const formatAnswerDate = (dateString: string) =>
	new Date(dateString).toLocaleString('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});

const getCompanyDisplayName = (company: {
	trade_name?: string | null;
	legal_name?: string | null;
}) =>
	company.trade_name?.trim() ||
	company.legal_name?.trim() ||
	'Empresa sem nome';

const FORM_TYPES_WITH_COMPANY_LINKS: Forms.FormType[] = [
	'EMPLOYEE_NR1',
	'CBI',
	'PET',
];

type FormAnswerQuestionMeta = {
	title: string;
	type: Forms.FormQuestionType;
	options?: Forms.FormQuestionOption[] | null;
	color: string | null;
	icon_identifier: string | null;
	category_name: string | null;
	subcategory_name: string | null;
};

const getAnswerRespondent = (item: Forms.FormAnswerAdminRow) => {
	const user = item.user ?? item.employee?.user ?? null;
	const name = [user?.first_name, user?.last_name]
		.filter(Boolean)
		.join(' ')
		.trim();
	const kind = item.user_id
		? 'Usuário'
		: item.employee_id
		  ? 'Colaborador'
		  : 'Anônimo';
	return { name: name || kind, email: user?.email ?? null, kind };
};

const splitAnswerKeys = (type: Forms.FormQuestionType, valueStr: string) =>
	type === 'MULTIPLE_CHOICE'
		? Array.from(
				new Set(
					valueStr
						.split('')
						.map((c) => c.trim())
						.filter(Boolean),
				),
		  )
		: [valueStr];

interface FormAnswersTableProps {
	items: Forms.FormAnswerAdminRow[];
	questions: Forms.FormQuestion[];
	questionMetaByNumber: Record<number, FormAnswerQuestionMeta>;
}

/**
 * Visão geral das respostas: uma linha por resposta, uma coluna por pergunta.
 * Alternativas aparecem como a letra da opção na cor cadastrada; o rótulo
 * completo fica no tooltip e na legenda abaixo da tabela.
 */
const FormAnswersTable = ({
	items,
	questions,
	questionMetaByNumber,
}: FormAnswersTableProps) => {
	const renderCell = (question: Forms.FormQuestion, rawValue: unknown) => {
		const meta = questionMetaByNumber[question.number];
		const valueStr = String(rawValue ?? '').trim();
		if (!valueStr) return <span className="text-stone-300">—</span>;

		if (!meta || meta.type === 'FREE_TEXT') {
			return (
				<span className="block max-w-[18rem] truncate" title={valueStr}>
					{valueStr}
				</span>
			);
		}

		const optionsByKey = (meta.options ?? []).reduce<
			Record<string, Forms.FormQuestionOption>
		>((acc, opt) => {
			acc[opt.key] = opt;
			return acc;
		}, {});

		return (
			<div className="flex flex-wrap gap-1">
				{splitAnswerKeys(meta.type, valueStr).map((key) => {
					const opt = optionsByKey[key];
					const hex =
						resolveColorInputToHex(
							(opt?.color ?? '').trim() || '#e5e7eb',
						) ?? '#e5e7eb';
					const textClass = getContrastTextClassForHex(hex);
					const score =
						typeof opt?.score === 'number'
							? ` · score ${opt.score}`
							: '';
					return (
						<span
							key={key}
							title={opt ? `${key}: ${opt.label}${score}` : key}
							className={cn(
								'inline-flex h-6 min-w-6 items-center justify-center rounded-md px-1.5 text-[11px] font-black',
								textClass,
							)}
							style={{ backgroundColor: hex }}
						>
							{key}
						</span>
					);
				})}
			</div>
		);
	};

	const questionsWithOptions = questions.filter(
		(q) => q.type !== 'FREE_TEXT' && (q.options ?? []).length > 0,
	);

	return (
		<div className="space-y-2">
			<div className="overflow-x-auto rounded-2xl border border-stone-100 bg-white shadow-sm">
				<table className="w-full min-w-max border-collapse text-left text-xs">
					<thead className="bg-stone-50 text-[10px] font-black uppercase tracking-widest text-stone-400">
						<tr>
							<th className="sticky left-0 z-10 bg-stone-50 px-3 py-2.5">
								Data
							</th>
							<th className="px-3 py-2.5">Respondente</th>
							{questions.map((q) => (
								<th
									key={q.id}
									className="max-w-[14rem] px-3 py-2.5 align-bottom"
									title={q.title}
								>
									<span className="block text-stone-900">
										{q.number}
									</span>
									<span className="block truncate font-bold normal-case tracking-normal text-stone-500">
										{q.title}
									</span>
								</th>
							))}
						</tr>
					</thead>
					<tbody className="divide-y divide-stone-100">
						{items.map((item) => {
							const respondent = getAnswerRespondent(item);
							return (
								<tr
									key={item.id}
									className="group/row hover:bg-stone-50/70"
								>
									<td className="sticky left-0 z-10 whitespace-nowrap bg-white px-3 py-2 font-semibold text-stone-700 group-hover/row:bg-stone-50">
										{formatAnswerDate(item.created_at)}
									</td>
									<td className="whitespace-nowrap px-3 py-2">
										<span className="block font-semibold text-stone-800">
											{respondent.name}
										</span>
										<span className="block text-[10px] text-stone-400">
											{respondent.email ??
												respondent.kind}
										</span>
									</td>
									{questions.map((q) => (
										<td
											key={q.id}
											className="px-3 py-2 align-top text-stone-900"
										>
											{renderCell(
												q,
												item.answer[String(q.number)],
											)}
										</td>
									))}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{questionsWithOptions.length > 0 ? (
				<details className="rounded-2xl border border-stone-100 bg-stone-50/60 px-4 py-2 text-xs">
					<summary className="cursor-pointer select-none text-[10px] font-black uppercase tracking-widest text-stone-400">
						Legenda das alternativas
					</summary>
					<div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
						{questionsWithOptions.map((q) => (
							<div key={q.id} className="space-y-1">
								<p className="font-bold text-stone-700">
									{q.number}. {q.title}
								</p>
								<ul className="space-y-0.5 text-stone-600">
									{(q.options ?? []).map((opt) => (
										<li key={opt.key}>
											<span className="font-black text-stone-900">
												{opt.key}
											</span>
											: {opt.label}
											{typeof opt.score === 'number'
												? ` (score ${opt.score})`
												: ''}
										</li>
									))}
								</ul>
							</div>
						))}
					</div>
				</details>
			) : null}
		</div>
	);
};

interface FormAnswersTabProps {
	formId: string;
	form: Forms.FormAdminDetail | undefined;
}

const FormAnswersTab = ({ formId, form }: FormAnswersTabProps) => {
	const [page, setPage] = useState(1);
	const [openCompanyIds, setOpenCompanyIds] = useState<
		Record<string, boolean>
	>({});
	const limit = 25;
	const { data, isFetching } = useGetFormAnswers(formId, { page, limit });
	const { data: formCategoriesResponse } = useGetFormCategories({
		page: 1,
		limit: 100,
	});
	const answers = data?.data ?? [];
	const pagination = data?.pagination;
	const categoriesCatalog = formCategoriesResponse?.data ?? [];
	const { data: companiesResponse } = useGetCompanies();
	const questionMetaByNumber = (form?.form_question ?? []).reduce(
		(acc, q) => {
			const questionCategory = q.category_id
				? categoriesCatalog.find(
						(category) => category.id === q.category_id,
				  ) ?? null
				: null;
			const questionSubcategory = q.subcategory_id
				? questionCategory?.form_subcategory.find(
						(subcategory) => subcategory.id === q.subcategory_id,
				  ) ??
				  categoriesCatalog
						.flatMap((category) => category.form_subcategory)
						.find(
							(subcategory) =>
								subcategory.id === q.subcategory_id,
						) ??
				  null
				: null;
			acc[q.number] = {
				title: q.title,
				type: q.type,
				options: q.options ?? undefined,
				color: q.color ?? null,
				icon_identifier: q.icon_identifier ?? null,
				category_name: questionCategory?.name ?? null,
				subcategory_name: questionSubcategory?.name ?? null,
			};
			return acc;
		},
		{} as Record<number, FormAnswerQuestionMeta>,
	);

	const companiesList = Array.isArray(companiesResponse)
		? companiesResponse
		: companiesResponse?.data ?? [];

	const companiesById =
		companiesList.reduce<Record<string, Companies.Company>>(
			(acc, company) => {
				acc[company.id] = company;
				return acc;
			},
			{},
		) ?? {};

	const companyFromLinksById =
		form?.form_audience
			?.filter(
				(audience) =>
					audience.entity_type === 'EMPLOYEE' && audience.company_id,
			)
			.reduce<Record<string, { name: string }>>((acc, audience) => {
				const companyId = audience.company_id!;
				acc[companyId] = {
					name: audience.company
						? getCompanyDisplayName(audience.company)
						: 'Empresa desconhecida',
				};
				return acc;
			}, {}) ?? {};

	const groupedByCompany = answers.reduce<
		Array<{ companyId: string; name: string; items: typeof answers }>
	>((groups, ans) => {
		const companyId = (ans as any).company_id ?? 'sem_empresa';
		let group = groups.find((g) => g.companyId === companyId);
		if (!group) {
			const baseName =
				companyId === 'sem_empresa'
					? 'Sem vínculo'
					: companiesById[companyId]?.trade_name ??
					  companiesById[companyId]?.legal_name ??
					  companyFromLinksById[companyId]?.name ??
					  'Empresa desconhecida';

			group = {
				companyId,
				name: baseName || 'Sem vínculo',
				items: [],
			};
			groups.push(group);
		}
		group.items.push(ans);
		return groups;
	}, []);

	const toggleCompany = (companyId: string) => {
		setOpenCompanyIds((prev) => ({
			...prev,
			[companyId]: !prev[companyId],
		}));
	};

	const setAllCompaniesOpenState = (open: boolean) => {
		const nextState: Record<string, boolean> = {};
		groupedByCompany.forEach((group) => {
			nextState[group.companyId] = open;
		});
		setOpenCompanyIds(nextState);
	};

	const areAllOpen =
		groupedByCompany.length > 0 &&
		groupedByCompany.every(
			(group) => (openCompanyIds[group.companyId] ?? true) === true,
		);

	const sortedQuestions = (form?.form_question ?? [])
		.slice()
		.sort((a, b) => a.number - b.number);

	return (
		<div className="mt-4 space-y-4">
			{isFetching ? (
				<p className="text-sm text-stone-400">
					Carregando respostas...
				</p>
			) : answers.length === 0 ? (
				<div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 py-12 text-center">
					<MessageSquare className="mx-auto h-10 w-10 text-stone-300" />
					<p className="mt-2 text-sm text-stone-400">
						Nenhuma resposta registrada para este formulário.
					</p>
				</div>
			) : (
				<>
					<div className="space-y-4">
						<div className="flex items-center justify-between">
							<h3 className="text-xs font-black uppercase tracking-[0.18em] text-stone-400">
								Respostas por empresa
							</h3>
							{groupedByCompany.length > 1 && (
								<button
									type="button"
									onClick={() =>
										setAllCompaniesOpenState(!areAllOpen)
									}
									className="flex h-7 w-7 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-500 hover:border-stone-300 hover:text-stone-900"
									aria-label={
										areAllOpen
											? 'Fechar todas as empresas'
											: 'Abrir todas as empresas'
									}
								>
									{areAllOpen ? (
										<FoldVertical size={14} />
									) : (
										<UnfoldVertical size={14} />
									)}
								</button>
							)}
						</div>
						{groupedByCompany.map((group) => {
							const isOpen =
								openCompanyIds[group.companyId] ?? true;

							const handleDownloadCsv = () => {
								const questions = (form?.form_question ?? [])
									.slice()
									.sort((a, b) => a.number - b.number);
								const questionTitles = questions.map(
									(q) => q.title,
								);

								const rows = group.items.map((item) => ({
									answers: questions.map((q) => {
										const rawValue =
											item.answer[String(q.number)];
										if (rawValue == null || rawValue === '')
											return null;

										const num = Number(rawValue);
										return Number.isFinite(num)
											? num
											: null;
									}),
								}));

								const safeName = group.name
									.replace(/[^a-zA-Z0-9À-ÿ ]/g, '')
									.trim()
									.replace(/\s+/g, '-');
								const now = new Date();
								const timestamp = now
									.toLocaleString('pt-BR', {
										day: '2-digit',
										month: '2-digit',
										year: 'numeric',
										hour: '2-digit',
										minute: '2-digit',
									})
									.replace(/[/:]/g, '-')
									.replace(/[, ]+/g, '_');
								downloadCsv({
									questions: questionTitles,
									rows,
									filename: `respostas-${safeName}_${timestamp}`,
								});
							};

							return (
								<div
									key={group.companyId}
									className="space-y-2"
								>
									<div className="flex w-full items-center justify-between rounded-2xl bg-stone-50 px-4 py-2">
										<button
											type="button"
											onClick={() =>
												toggleCompany(group.companyId)
											}
											className="flex min-w-0 flex-1 items-center gap-2 text-left text-xs font-black uppercase tracking-[0.18em] text-stone-500 hover:text-stone-700"
										>
											<span className="inline-flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-stone-900 text-[10px] font-black text-white">
												{(() => {
													if (
														group.companyId ===
														'sem_empresa'
													) {
														return group.name
															.charAt(0)
															.toUpperCase();
													}
													const logo =
														companiesById[
															group.companyId
														]?.logo_img_url ?? null;
													if (logo) {
														return (
															<img
																src={logo}
																alt={group.name}
																className="h-full w-full object-cover"
															/>
														);
													}
													return group.name
														.charAt(0)
														.toUpperCase();
												})()}
											</span>
											<span className="text-[11px] font-black text-stone-700">
												{group.name}
											</span>
										</button>
										<div className="flex items-center gap-1">
											<button
												type="button"
												onClick={handleDownloadCsv}
												className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-stone-900 px-2.5 py-2 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm shadow-stone-100/30 transition-colors hover:bg-stone-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
												title={`Baixar CSV de ${group.name}`}
											>
												<Download
													className="size-3.5"
													aria-hidden
												/>
												Baixar CSV
											</button>
											<button
												type="button"
												onClick={() =>
													toggleCompany(
														group.companyId,
													)
												}
												className="flex h-7 w-7 items-center justify-center rounded-full text-stone-400 transition-colors hover:bg-stone-200 hover:text-stone-700"
											>
												<ChevronDown
													size={14}
													className={`transition-transform ${
														isOpen
															? 'rotate-180'
															: 'rotate-0'
													}`}
												/>
											</button>
										</div>
									</div>

									{isOpen && (
										<FormAnswersTable
											items={group.items}
											questions={sortedQuestions}
											questionMetaByNumber={
												questionMetaByNumber
											}
										/>
									)}
								</div>
							);
						})}
					</div>
					{pagination && pagination.total > limit && (
						<div className="flex justify-center pt-4">
							<DataTablePagination
								page={page}
								pageSize={pagination.limit}
								totalItems={pagination.total}
								onPageChange={setPage}
							/>
						</div>
					)}
				</>
			)}
		</div>
	);
};

interface FormCompaniesTabProps {
	formId: string;
	form: Forms.FormAdminDetail | undefined;
}

const AddCompanyToFormModal = ({
	isOpen,
	onClose,
	formId,
	linkedCompanyIds,
}: {
	isOpen: boolean;
	onClose: () => void;
	formId: string;
	linkedCompanyIds: string[];
}) => {
	const { data: companiesResponse, isLoading } = useGetCompanies();
	const { mutateAsync: addAudience, isPending } = useAddFormAudience(
		formId,
		onClose,
	);
	const list = Array.isArray(companiesResponse)
		? companiesResponse
		: companiesResponse?.data ?? [];
	const available = list.filter(
		(c: Companies.Company) => !linkedCompanyIds.includes(c.id),
	);

	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-6 py-6">
					<h2 className="text-lg font-black text-stone-900">
						Vincular empresa ao formulário
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="text-stone-400 transition-colors hover:text-stone-600"
					>
						<X size={20} />
					</button>
				</div>
				<div className="flex-1 overflow-y-auto p-6">
					{isLoading ? (
						<div className="flex justify-center py-12">
							<Loader2 className="size-8 animate-spin text-stone-300" />
						</div>
					) : available.length === 0 ? (
						<div className="flex flex-col items-center py-12 text-center">
							<Building2 className="mx-auto h-10 w-10 text-stone-300" />
							<p className="mt-2 text-sm font-bold text-stone-600">
								Nenhuma empresa disponível
							</p>
							<p className="mt-1 text-xs text-stone-400">
								Todas as empresas já estão vinculadas ou não há
								empresas cadastradas.
							</p>
						</div>
					) : (
						<div className="space-y-2">
							{available.map((company: Companies.Company) => (
								<div
									key={company.id}
									className="flex items-center justify-between rounded-2xl border border-stone-100 bg-stone-50/50 p-4 transition-all hover:border-stone-200 hover:bg-white"
								>
									<div className="flex min-w-0 flex-1 items-center gap-3">
										<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-stone-200 text-stone-600">
											<Building2 size={20} />
										</div>
										<span className="text-sm font-bold text-stone-900">
											{getCompanyDisplayName(company)}
										</span>
									</div>
									<Button
										text={
											isPending
												? 'Vinculando...'
												: 'Vincular'
										}
										disabled={isPending}
										className="shrink-0 rounded-full bg-black px-4 py-2 text-xs font-bold text-white shadow-md hover:bg-neutral-800 disabled:opacity-70"
										onClick={() =>
											addAudience({
												entity_type: 'EMPLOYEE',
												company_id: company.id,
											})
										}
									/>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
};

const UnlinkCompanyModal = ({
	isOpen,
	onClose,
	onConfirm,
	companyName,
	isUnlinking,
}: {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	companyName: string;
	isUnlinking?: boolean;
}) => {
	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative w-full max-w-[400px] rounded-[2rem] bg-white p-8 text-center shadow-2xl duration-200">
				<div className="mb-4 flex justify-center text-rose-500">
					<div className="rounded-full bg-rose-50 p-4">
						<AlertTriangle size={32} />
					</div>
				</div>
				<h3 className="mb-2 text-xl font-black text-stone-900">
					Desvincular empresa
				</h3>
				<p
					className="mb-8 text-sm font-medium leading-relaxed text-stone-500"
					dangerouslySetInnerHTML={{
						__html: `Tem certeza que deseja desvincular a empresa <strong>${companyName}</strong> deste formulário?`,
					}}
				/>
				<div className="flex flex-col gap-3">
					<Button
						text={
							isUnlinking
								? 'Desvinculando...'
								: 'Confirmar desvinculação'
						}
						disabled={isUnlinking}
						className="w-full justify-center rounded-full border-none bg-rose-600 py-3 font-bold text-white shadow-md hover:bg-rose-700 disabled:opacity-70"
						onClick={onConfirm}
					/>
					<button
						onClick={onClose}
						disabled={isUnlinking}
						className="text-sm font-bold text-stone-400 hover:text-stone-600 disabled:opacity-50"
					>
						Cancelar
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};

const FormCompaniesTab = ({ formId, form }: FormCompaniesTabProps) => {
	const [isAddOpen, setIsAddOpen] = useState(false);
	const [audienceToRemove, setAudienceToRemove] =
		useState<Forms.FormAudience | null>(null);

	const { mutateAsync: removeAudience, isPending: isUnlinking } =
		useRemoveFormAudience(formId, () => setAudienceToRemove(null));

	const linked =
		form?.form_audience?.filter(
			(audience) => audience.entity_type === 'EMPLOYEE',
		) ?? [];
	const linkedIds = linked
		.map((audience) => audience.company_id)
		.filter((id): id is string => !!id);

	const handleUnlinkConfirm = async () => {
		if (audienceToRemove) await removeAudience(audienceToRemove.id);
	};

	return (
		<div className="mt-4 space-y-4">
			<div className="flex items-center justify-between">
				<h3 className="text-sm font-black text-stone-900">
					Empresas vinculadas
				</h3>
				<Button
					text="Vincular empresa"
					className="rounded-full bg-black px-4 py-2 text-xs font-bold shadow-md hover:bg-neutral-800"
					startIcon={<Plus className="h-4 w-4 text-white" />}
					onClick={() => setIsAddOpen(true)}
				/>
			</div>
			{linked.length === 0 ? (
				<div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 py-16 text-center">
					<Building2 className="mb-3 h-10 w-10 text-stone-300" />
					<p className="text-sm font-bold text-stone-600">
						Nenhuma empresa vinculada
					</p>
					<p className="mt-1 text-xs text-stone-400">
						Vincule empresas para que colaboradores possam responder
						a este formulário.
					</p>
					<Button
						text="Vincular empresa"
						variant="outline"
						className="mt-4 rounded-full border-stone-300 px-5 py-2 font-bold"
						startIcon={<Plus className="size-4 stroke-[3]" />}
						onClick={() => setIsAddOpen(true)}
					/>
				</div>
			) : (
				<div className="space-y-2">
					{linked.map((audience) => (
						<div
							key={audience.id}
							className="group flex items-center justify-between rounded-2xl border border-stone-100 bg-white p-4 shadow-sm transition-all hover:border-stone-200"
						>
							<div className="flex items-center gap-4">
								<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-stone-100 text-stone-600">
									<Building2 size={24} />
								</div>
								<div className="flex flex-col">
									<span className="text-sm font-bold leading-tight text-stone-900">
										{audience.company
											? getCompanyDisplayName(
													audience.company,
											  )
											: 'Empresa desconhecida'}
									</span>
									<span className="text-[10px] font-bold uppercase tracking-tight text-stone-400">
										Empresa vinculada
									</span>
								</div>
							</div>
							<button
								type="button"
								onClick={() => setAudienceToRemove(audience)}
								className="rounded-lg p-2 text-stone-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
								title="Desvincular"
							>
								<Trash2 size={16} />
							</button>
						</div>
					))}
				</div>
			)}

			<AddCompanyToFormModal
				isOpen={isAddOpen}
				onClose={() => setIsAddOpen(false)}
				formId={formId}
				linkedCompanyIds={linkedIds}
			/>

			<UnlinkCompanyModal
				isOpen={!!audienceToRemove}
				onClose={() => setAudienceToRemove(null)}
				onConfirm={handleUnlinkConfirm}
				companyName={
					audienceToRemove?.company
						? getCompanyDisplayName(audienceToRemove.company)
						: ''
				}
				isUnlinking={isUnlinking}
			/>
		</div>
	);
};

interface EditFormProps {
	/** Usa este id em vez do parâmetro de rota (ex.: página Admin › Match). */
	formIdOverride?: string;
	/** Embutido em outra página: esconde cabeçalho/voltar e o padding externo. */
	embedded?: boolean;
}

const EditForm = ({ formIdOverride, embedded = false }: EditFormProps = {}) => {
	const { id: routeId } = useParams<{ id: string }>();
	const id = formIdOverride ?? routeId;
	const navigate = useNavigate();

	const { data: form, isLoading } = useGetForm(id || '');
	const { data: formCategoriesResponse } = useGetFormCategories({
		page: 1,
		limit: 100,
	});
	const { mutate: createQuestion } = useCreateFormQuestion();
	const { mutate: patchQuestion } = usePatchFormQuestion();
	const { mutate: deleteQuestion } = useRemoveFormQuestion();
	const { mutate: reorderQuestions, isPending: isReordering } =
		useReorderFormQuestions();

	const [newTitle, setNewTitle] = useState('');
	const [newType, setNewType] =
		useState<Forms.FormQuestionType>('SINGLE_CHOICE');
	const [newCategoryValue, setNewCategoryValue] = useState(NO_CATEGORY_VALUE);
	const [newSubcategoryValue, setNewSubcategoryValue] =
		useState(NO_SUBCATEGORY_VALUE);
	const [newDescription, setNewDescription] = useState('');
	const [newColor, setNewColor] = useState('#111827');
	const [newIconIdentifier, setNewIconIdentifier] = useState('');
	const [newOptions, setNewOptions] = useState<
		Array<{
			key: string;
			label: string;
			score?: number;
			color?: string;
			icon_identifier?: string;
		}>
	>(getDefaultOptions());

	const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
		null,
	);
	const [editTitle, setEditTitle] = useState('');
	const [editType, setEditType] =
		useState<Forms.FormQuestionType>('FREE_TEXT');
	const [editCategoryValue, setEditCategoryValue] =
		useState(NO_CATEGORY_VALUE);
	const [editSubcategoryValue, setEditSubcategoryValue] =
		useState(NO_SUBCATEGORY_VALUE);
	const [editDescription, setEditDescription] = useState('');
	const [editColor, setEditColor] = useState('');
	const [editIconIdentifier, setEditIconIdentifier] = useState('');
	const [editOptions, setEditOptions] = useState<
		Array<{
			key: string;
			label: string;
			score?: number;
			color?: string;
			icon_identifier?: string;
		}>
	>([]);

	if (!id) {
		return null;
	}

	/** Move a pergunta uma posição para cima/baixo e salva a nova ordem (number = índice). */
	const moveQuestion = (questionId: string, delta: -1 | 1) => {
		if (!form || isReordering) return;
		const ordered = [...form.form_question]
			.sort((a, b) => a.number - b.number)
			.map((q) => q.id);
		const from = ordered.indexOf(questionId);
		const to = from + delta;
		if (from < 0 || to < 0 || to >= ordered.length) return;
		const next = [...ordered];
		const [moved] = next.splice(from, 1);
		next.splice(to, 0, moved);
		reorderQuestions({ formId: id, body: { ordered_ids: next } });
	};

	const getNextOptionKey = (used: string[]): string => {
		// Incrementa de acordo com a regra:
		// A..Z, ZA..ZZ, ZZA..ZZZ, ZZZ A.. (ZZZA), e assim por diante.
		const nextKeyAfter = (current: string): string => {
			if (!current) return 'A';
			const last = current[current.length - 1];
			if (last !== 'Z') {
				// incrementa a última letra (ex: A -> B, Y -> Z, ZA -> ZB)
				return (
					current.slice(0, -1) +
					String.fromCharCode(last.charCodeAt(0) + 1)
				);
			}
			// última letra é Z
			if (current.length === 1) {
				// Z -> ZA
				return 'ZA';
			}
			// ZZ -> ZZA, ZZZ -> ZZZA, etc.
			return current + 'A';
		};

		// Começa em A e vai avançando até achar um código livre
		let candidate = 'A';
		const maxIterations = 2000;
		let i = 0;
		while (used.includes(candidate) && i < maxIterations) {
			candidate = nextKeyAfter(candidate);
			i += 1;
		}
		return candidate;
	};

	const needsOptions =
		newType === 'SINGLE_CHOICE' || newType === 'MULTIPLE_CHOICE';
	const categoriesCatalog = formCategoriesResponse?.data ?? [];
	const selectedNewCategory =
		newCategoryValue === NO_CATEGORY_VALUE
			? null
			: categoriesCatalog.find(
					(category) => category.id === newCategoryValue,
			  ) ?? null;
	const availableNewSubcategories =
		selectedNewCategory?.form_subcategory ?? [];
	const selectedNewSubcategory =
		newSubcategoryValue === NO_SUBCATEGORY_VALUE
			? null
			: availableNewSubcategories.find(
					(subcategory) => subcategory.id === newSubcategoryValue,
			  ) ?? null;
	const selectedEditCategory =
		editCategoryValue === NO_CATEGORY_VALUE
			? null
			: categoriesCatalog.find(
					(category) => category.id === editCategoryValue,
			  ) ?? null;
	const availableEditSubcategories =
		selectedEditCategory?.form_subcategory ?? [];
	const selectedEditSubcategory =
		editSubcategoryValue === NO_SUBCATEGORY_VALUE
			? null
			: availableEditSubcategories.find(
					(subcategory) => subcategory.id === editSubcategoryValue,
			  ) ?? null;
	const nextNumber = (form?.form_question?.length ?? 0) + 1;
	const hasVinculosTab = form
		? FORM_TYPES_WITH_COMPANY_LINKS.includes(form.type)
		: false;

	const addNewOption = () => {
		const used = newOptions.map((o) => o.key);
		const next = getNextOptionKey(used);
		setNewOptions([
			...newOptions,
			{
				key: next,
				label: '',
				score: undefined,
				color: undefined,
				icon_identifier: undefined,
			},
		]);
	};

	const removeNewOption = (index: number) => {
		setNewOptions(newOptions.filter((_, i) => i !== index));
	};

	const updateNewOption = (
		index: number,
		field: 'key' | 'label' | 'score' | 'color' | 'icon_identifier',
		value: string | number | undefined,
	) => {
		const next = [...newOptions];
		next[index] = { ...next[index], [field]: value };
		setNewOptions(next);
	};

	const buildOptionsPayload = (
		opts: Array<{
			key: string;
			label: string;
			score?: number;
			color?: string;
			icon_identifier?: string;
		}>,
	): Forms.FormQuestionOption[] | undefined => {
		const filtered = opts.filter((o) => o.key.trim() && o.label.trim());
		if (filtered.length === 0) return undefined;
		return filtered.map((o) => {
			const color = o.color?.trim();
			const icon_identifier = o.icon_identifier?.trim();
			const normalizedScore =
				typeof o.score === 'number' && Number.isFinite(o.score)
					? Math.max(0, Math.trunc(o.score))
					: undefined;
			return {
				key: o.key.trim(),
				label: o.label.trim(),
				...(normalizedScore !== undefined
					? { score: normalizedScore }
					: {}),
				...(color ? { color } : {}),
				...(icon_identifier ? { icon_identifier } : {}),
			};
		});
	};

	const handleCreateQuestion = () => {
		if (!newTitle.trim()) return;
		const options = needsOptions
			? buildOptionsPayload(newOptions)
			: undefined;
		if (needsOptions && (!options || options.length === 0)) return;
		const category_id =
			newCategoryValue !== NO_CATEGORY_VALUE
				? newCategoryValue
				: undefined;
		const subcategory_id =
			category_id && newSubcategoryValue !== NO_SUBCATEGORY_VALUE
				? newSubcategoryValue
				: undefined;

		createQuestion({
			formId: id,
			body: {
				number: nextNumber,
				title: newTitle.trim(),
				type: newType,
				description: newDescription.trim() || undefined,
				color: newColor.trim() || undefined,
				icon_identifier: newIconIdentifier.trim() || undefined,
				...(category_id ? { category_id } : {}),
				...(subcategory_id ? { subcategory_id } : {}),
				options,
			},
		});
		setNewTitle('');
		setNewDescription('');
		setNewColor('#111827');
		setNewIconIdentifier('');
		setNewType('SINGLE_CHOICE');
		setNewCategoryValue(NO_CATEGORY_VALUE);
		setNewSubcategoryValue(NO_SUBCATEGORY_VALUE);
		setNewOptions(getDefaultOptions());
	};

	const startEditing = (q: Forms.FormQuestion) => {
		setEditingQuestionId(q.id);
		setEditTitle(q.title);
		setEditType(q.type);
		setEditCategoryValue(q.category_id ?? NO_CATEGORY_VALUE);
		setEditSubcategoryValue(q.subcategory_id ?? NO_SUBCATEGORY_VALUE);
		setEditDescription(q.description ?? '');
		setEditColor(q.color ?? '#111827');
		const opts =
			Array.isArray(q.options) && q.options.length > 0
				? q.options.map((o) => ({
						key: o.key,
						label: o.label,
						score: o.score ?? undefined,
						color: o.color ?? undefined,
						icon_identifier: o.icon_identifier ?? undefined,
				  }))
				: getDefaultOptions();
		setEditOptions(opts);
		setEditIconIdentifier(q.icon_identifier ?? '');
	};

	const cancelEditing = () => {
		setEditingQuestionId(null);
	};

	const handlePatchQuestion = () => {
		if (!editingQuestionId) return;
		const editNeedsOptions =
			editType === 'SINGLE_CHOICE' || editType === 'MULTIPLE_CHOICE';
		const options = editNeedsOptions
			? buildOptionsPayload(editOptions)
			: [];
		if (editNeedsOptions && (!options || options.length === 0)) return;
		const category_id =
			editCategoryValue !== NO_CATEGORY_VALUE
				? editCategoryValue
				: undefined;
		const subcategory_id =
			category_id && editSubcategoryValue !== NO_SUBCATEGORY_VALUE
				? editSubcategoryValue
				: undefined;

		patchQuestion({
			formId: id,
			questionId: editingQuestionId,
			body: {
				title: editTitle.trim(),
				type: editType,
				description: editDescription.trim() || undefined,
				color: editColor.trim() || undefined,
				icon_identifier: editIconIdentifier.trim() || undefined,
				...(category_id ? { category_id } : {}),
				...(subcategory_id ? { subcategory_id } : {}),
				options: options?.length ? options : [],
			},
		});
		setEditingQuestionId(null);
	};

	const addEditOption = () => {
		const used = editOptions.map((o) => o.key);
		const next = getNextOptionKey(used);
		setEditOptions([
			...editOptions,
			{
				key: next,
				label: '',
				score: undefined,
				color: undefined,
				icon_identifier: undefined,
			},
		]);
	};

	const removeEditOption = (index: number) => {
		setEditOptions(editOptions.filter((_, i) => i !== index));
	};

	const updateEditOption = (
		index: number,
		field: 'key' | 'label' | 'score' | 'color' | 'icon_identifier',
		value: string | number | undefined,
	) => {
		const next = [...editOptions];
		next[index] = { ...next[index], [field]: value };
		setEditOptions(next);
	};

	const labelClass =
		'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-400';
	const inputClass =
		'w-full rounded-xl border border-stone-200 bg-stone-50/50 px-4 py-3 text-sm font-medium text-stone-700 outline-none focus:border-stone-400 focus:bg-white';

	return (
		<section className={embedded ? 'space-y-6' : 'space-y-6 px-8 py-8'}>
			{!embedded ? (
				<div className="flex items-center gap-4">
					<button
						type="button"
						onClick={() => navigate('/admin/formularios')}
						className="group flex h-10 w-10 items-center justify-center rounded-full border border-stone-100 bg-white transition-all hover:border-stone-200"
					>
						<ArrowLeft
							size={18}
							className="text-stone-400 group-hover:text-stone-900"
						/>
					</button>
					<div className="space-y-1">
						<PageTitle text={form?.title || 'Editar formulário'} />
						<p className="text-sm text-stone-400">
							{hasVinculosTab
								? 'Gerencie as perguntas e vínculos deste formulário.'
								: 'Gerencie as perguntas deste formulário.'}
						</p>
					</div>
				</div>
			) : null}

			{isLoading || !form ? (
				<p className="text-sm text-stone-400">
					Carregando formulário...
				</p>
			) : (
				<SlidingTabs
					defaultActiveTab="perguntas"
					tabs={[
						{
							id: 'perguntas',
							label: 'Perguntas',
							children: (
								<div className="mt-4 space-y-6">
									{/* Nova pergunta */}
									<div className="space-y-3">
										<h3 className="text-sm font-black text-stone-900">
											Nova pergunta
										</h3>
										<div className="rounded-[2rem] border border-stone-100 bg-white p-6 shadow-sm">
											<div className="space-y-4">
												<div className="space-y-2">
													<label
														className={labelClass}
													>
														Título da pergunta
													</label>
													<input
														type="text"
														value={newTitle}
														onChange={(e) =>
															setNewTitle(
																e.target.value,
															)
														}
														placeholder="Ex: Qual seu nível de satisfação?"
														className={inputClass}
													/>
												</div>

												<div className="space-y-2">
													<label
														className={labelClass}
													>
														Categoria
													</label>
													<Select
														value={newCategoryValue}
														onValueChange={(
															value,
														) => {
															setNewCategoryValue(
																value,
															);
															setNewSubcategoryValue(
																NO_SUBCATEGORY_VALUE,
															);
														}}
													>
														<SelectTrigger
															className={
																inputClass
															}
														>
															<SelectValue>
																{newCategoryValue ===
																NO_CATEGORY_VALUE
																	? 'Sem categoria'
																	: selectedNewCategory?.name ??
																	  'Sem categoria'}
															</SelectValue>
														</SelectTrigger>
														<SelectContent>
															<SelectItem
																value={
																	NO_CATEGORY_VALUE
																}
															>
																Sem categoria
															</SelectItem>
															{categoriesCatalog.map(
																(category) => (
																	<SelectItem
																		key={
																			category.id
																		}
																		value={
																			category.id
																		}
																	>
																		{
																			category.name
																		}
																	</SelectItem>
																),
															)}
														</SelectContent>
													</Select>
												</div>

												<div className="space-y-2">
													<label
														className={labelClass}
													>
														Subcategoria
													</label>
													<Select
														value={
															newSubcategoryValue
														}
														onValueChange={(
															value,
														) =>
															setNewSubcategoryValue(
																value,
															)
														}
														disabled={
															newCategoryValue ===
															NO_CATEGORY_VALUE
														}
													>
														<SelectTrigger
															className={
																inputClass
															}
														>
															<SelectValue>
																{newSubcategoryValue ===
																NO_SUBCATEGORY_VALUE
																	? 'Sem subcategoria'
																	: selectedNewSubcategory?.name ??
																	  'Sem subcategoria'}
															</SelectValue>
														</SelectTrigger>
														<SelectContent>
															<SelectItem
																value={
																	NO_SUBCATEGORY_VALUE
																}
															>
																Sem subcategoria
															</SelectItem>
															{availableNewSubcategories.map(
																(
																	subcategory,
																) => (
																	<SelectItem
																		key={
																			subcategory.id
																		}
																		value={
																			subcategory.id
																		}
																	>
																		{
																			subcategory.name
																		}
																	</SelectItem>
																),
															)}
														</SelectContent>
													</Select>
												</div>

												<div className="space-y-2">
													<label
														className={labelClass}
													>
														Tipo de pergunta
													</label>
													<Select
														value={newType}
														onValueChange={(v) =>
															setNewType(
																v as Forms.FormQuestionType,
															)
														}
													>
														<SelectTrigger
															className={
																inputClass
															}
														>
															<SelectValue>
																{questionTypeLabel(
																	newType,
																)}
															</SelectValue>
														</SelectTrigger>
														<SelectContent>
															{QUESTION_TYPE_OPTIONS.map(
																(opt) => (
																	<SelectItem
																		key={
																			opt.value
																		}
																		value={
																			opt.value
																		}
																	>
																		{
																			opt.label
																		}
																	</SelectItem>
																),
															)}
														</SelectContent>
													</Select>
												</div>

												<div className="space-y-2">
													<label
														className={labelClass}
													>
														Descrição{' '}
														<span className="text-stone-300">
															(opcional)
														</span>
													</label>
													<textarea
														value={newDescription}
														onChange={(e) =>
															setNewDescription(
																e.target.value,
															)
														}
														placeholder="Texto de apoio ou instrução"
														rows={2}
														className={inputClass}
													/>
												</div>

												<QuestionColorPicker
													label="Cor e ícone da pergunta (opcional)"
													value={newColor}
													onChange={setNewColor}
													iconValue={
														newIconIdentifier
													}
													iconOnChange={
														setNewIconIdentifier
													}
													labelClass={labelClass}
													inputClass={inputClass}
												/>

												{needsOptions && (
													<div className="space-y-2">
														<div className="flex items-center justify-between">
															<label
																className={
																	labelClass
																}
															>
																Opções de
																resposta
															</label>
															<button
																type="button"
																onClick={
																	addNewOption
																}
																className="text-xs font-bold text-stone-500 hover:text-stone-700"
															>
																+ Adicionar
																opção
															</button>
														</div>
														<div className="space-y-2">
															{newOptions.map(
																(opt, idx) => {
																	const colorValue =
																		opt.color ??
																		'#e5e7eb';

																	return (
																		<div
																			key={
																				idx
																			}
																			className="flex flex-wrap items-center gap-2"
																		>
																			<input
																				type="text"
																				value={
																					opt.key
																				}
																				onChange={(
																					e,
																				) =>
																					updateNewOption(
																						idx,
																						'key',
																						e
																							.target
																							.value,
																					)
																				}
																				placeholder="Chave (ex: A)"
																				className="w-16 rounded-lg border border-stone-200 bg-white px-2 py-2 text-sm"
																			/>
																			<input
																				type="text"
																				value={
																					opt.label
																				}
																				onChange={(
																					e,
																				) =>
																					updateNewOption(
																						idx,
																						'label',
																						e
																							.target
																							.value,
																					)
																				}
																				placeholder="Texto da opção"
																				className="min-w-[180px] flex-1 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm"
																			/>
																			<OptionScoreInput
																				value={
																					opt.score
																				}
																				onChange={(
																					value,
																				) =>
																					updateNewOption(
																						idx,
																						'score',
																						value,
																					)
																				}
																			/>
																			<LucideIconPopover
																				value={
																					opt.icon_identifier ??
																					''
																				}
																				onChange={(
																					v,
																				) =>
																					updateNewOption(
																						idx,
																						'icon_identifier',
																						v,
																					)
																				}
																			/>
																			<ColorPopover
																				value={
																					colorValue
																				}
																				onChange={(
																					v,
																				) =>
																					updateNewOption(
																						idx,
																						'color',
																						v,
																					)
																				}
																			/>

																			<button
																				type="button"
																				onClick={() =>
																					removeNewOption(
																						idx,
																					)
																				}
																				className="rounded-full p-2 text-stone-400 hover:bg-rose-50 hover:text-rose-600"
																			>
																				<Trash2
																					size={
																						14
																					}
																				/>
																			</button>
																		</div>
																	);
																},
															)}
														</div>
													</div>
												)}

												<div className="flex justify-end pt-2">
													<Button
														text="Criar pergunta"
														className="rounded-full bg-black px-5 py-2 text-xs font-bold shadow-md hover:bg-neutral-800"
														startIcon={
															<Plus className="h-4 w-4 text-white" />
														}
														onClick={
															handleCreateQuestion
														}
														disabled={
															!newTitle.trim() ||
															(needsOptions &&
																newOptions.every(
																	(o) =>
																		!o.key.trim() ||
																		!o.label.trim(),
																))
														}
													/>
												</div>
											</div>
										</div>
									</div>

									{/* Lista de perguntas */}
									<div>
										<h3 className="mb-3 text-sm font-black text-stone-900">
											Perguntas do formulário
										</h3>
										<div className="space-y-2">
											{form.form_question.map((q) => {
												const rawQuestionColor =
													q.color?.trim() ?? '';
												const resolvedQuestionHex =
													resolveColorInputToHex(
														rawQuestionColor,
													) ?? '#e5e7eb';
												const questionTextColorClass =
													getContrastTextClassForHex(
														resolvedQuestionHex,
													);
												const questionCategory =
													q.category_id
														? categoriesCatalog.find(
																(category) =>
																	category.id ===
																	q.category_id,
														  ) ?? null
														: null;
												const questionSubcategory =
													q.subcategory_id
														? questionCategory?.form_subcategory.find(
																(subcategory) =>
																	subcategory.id ===
																	q.subcategory_id,
														  ) ??
														  categoriesCatalog
																.flatMap(
																	(
																		category,
																	) =>
																		category.form_subcategory,
																)
																.find(
																	(
																		subcategory,
																	) =>
																		subcategory.id ===
																		q.subcategory_id,
																) ??
														  null
														: null;

												return (
													<div
														key={q.id}
														className="rounded-2xl border border-stone-100 bg-white shadow-sm"
													>
														{editingQuestionId ===
														q.id ? (
															<div className="space-y-4 p-4">
																<div className="flex items-center justify-between">
																	<span className="text-[10px] font-black uppercase tracking-widest text-stone-400">
																		{
																			q.number
																		}
																		. Editar
																	</span>
																	<button
																		type="button"
																		onClick={
																			cancelEditing
																		}
																		className="rounded-full p-2 text-stone-400 hover:bg-stone-100"
																	>
																		<X
																			size={
																				16
																			}
																		/>
																	</button>
																</div>
																<div className="space-y-2">
																	<label
																		className={
																			labelClass
																		}
																	>
																		Título
																	</label>
																	<input
																		type="text"
																		value={
																			editTitle
																		}
																		onChange={(
																			e,
																		) =>
																			setEditTitle(
																				e
																					.target
																					.value,
																			)
																		}
																		className={
																			inputClass
																		}
																	/>
																</div>
																<div className="space-y-2">
																	<label
																		className={
																			labelClass
																		}
																	>
																		Categoria
																	</label>
																	<Select
																		value={
																			editCategoryValue
																		}
																		onValueChange={(
																			value,
																		) => {
																			setEditCategoryValue(
																				value,
																			);
																			setEditSubcategoryValue(
																				NO_SUBCATEGORY_VALUE,
																			);
																		}}
																	>
																		<SelectTrigger
																			className={
																				inputClass
																			}
																		>
																			<SelectValue>
																				{editCategoryValue ===
																				NO_CATEGORY_VALUE
																					? 'Sem categoria'
																					: selectedEditCategory?.name ??
																					  'Sem categoria'}
																			</SelectValue>
																		</SelectTrigger>
																		<SelectContent>
																			<SelectItem
																				value={
																					NO_CATEGORY_VALUE
																				}
																			>
																				Sem
																				categoria
																			</SelectItem>
																			{categoriesCatalog.map(
																				(
																					category,
																				) => (
																					<SelectItem
																						key={
																							category.id
																						}
																						value={
																							category.id
																						}
																					>
																						{
																							category.name
																						}
																					</SelectItem>
																				),
																			)}
																		</SelectContent>
																	</Select>
																</div>
																<div className="space-y-2">
																	<label
																		className={
																			labelClass
																		}
																	>
																		Subcategoria
																	</label>
																	<Select
																		value={
																			editSubcategoryValue
																		}
																		onValueChange={(
																			value,
																		) =>
																			setEditSubcategoryValue(
																				value,
																			)
																		}
																		disabled={
																			editCategoryValue ===
																			NO_CATEGORY_VALUE
																		}
																	>
																		<SelectTrigger
																			className={
																				inputClass
																			}
																		>
																			<SelectValue>
																				{editSubcategoryValue ===
																				NO_SUBCATEGORY_VALUE
																					? 'Sem subcategoria'
																					: selectedEditSubcategory?.name ??
																					  'Sem subcategoria'}
																			</SelectValue>
																		</SelectTrigger>
																		<SelectContent>
																			<SelectItem
																				value={
																					NO_SUBCATEGORY_VALUE
																				}
																			>
																				Sem
																				subcategoria
																			</SelectItem>
																			{availableEditSubcategories.map(
																				(
																					subcategory,
																				) => (
																					<SelectItem
																						key={
																							subcategory.id
																						}
																						value={
																							subcategory.id
																						}
																					>
																						{
																							subcategory.name
																						}
																					</SelectItem>
																				),
																			)}
																		</SelectContent>
																	</Select>
																</div>
																<div className="space-y-2">
																	<label
																		className={
																			labelClass
																		}
																	>
																		Tipo
																	</label>
																	<Select
																		value={
																			editType
																		}
																		onValueChange={(
																			v,
																		) =>
																			setEditType(
																				v as Forms.FormQuestionType,
																			)
																		}
																	>
																		<SelectTrigger
																			className={
																				inputClass
																			}
																		>
																			<SelectValue>
																				{questionTypeLabel(
																					editType,
																				)}
																			</SelectValue>
																		</SelectTrigger>
																		<SelectContent>
																			{QUESTION_TYPE_OPTIONS.map(
																				(
																					opt,
																				) => (
																					<SelectItem
																						key={
																							opt.value
																						}
																						value={
																							opt.value
																						}
																					>
																						{
																							opt.label
																						}
																					</SelectItem>
																				),
																			)}
																		</SelectContent>
																	</Select>
																</div>
																<div className="space-y-2">
																	<label
																		className={
																			labelClass
																		}
																	>
																		Descrição
																		(opcional)
																	</label>
																	<textarea
																		value={
																			editDescription
																		}
																		onChange={(
																			e,
																		) =>
																			setEditDescription(
																				e
																					.target
																					.value,
																			)
																		}
																		rows={2}
																		className={
																			inputClass
																		}
																	/>
																</div>
																<QuestionColorPicker
																	label="Cor e ícone da pergunta (opcional)"
																	value={
																		editColor
																	}
																	onChange={
																		setEditColor
																	}
																	iconValue={
																		editIconIdentifier
																	}
																	iconOnChange={
																		setEditIconIdentifier
																	}
																	labelClass={
																		labelClass
																	}
																	inputClass={
																		inputClass
																	}
																/>
																{(editType ===
																	'SINGLE_CHOICE' ||
																	editType ===
																		'MULTIPLE_CHOICE') && (
																	<div className="space-y-2">
																		<div className="flex items-center justify-between">
																			<label
																				className={
																					labelClass
																				}
																			>
																				Opções
																			</label>
																			<button
																				type="button"
																				onClick={
																					addEditOption
																				}
																				className="text-xs font-bold text-stone-500 hover:text-stone-700"
																			>
																				+
																				Adicionar
																			</button>
																		</div>
																		{editOptions.map(
																			(
																				opt,
																				idx,
																			) => {
																				const colorValue =
																					opt.color ??
																					'#e5e7eb';
																				return (
																					<div
																						key={
																							idx
																						}
																						className="flex flex-wrap items-center gap-2"
																					>
																						<input
																							type="text"
																							value={
																								opt.key
																							}
																							onChange={(
																								e,
																							) =>
																								updateEditOption(
																									idx,
																									'key',
																									e
																										.target
																										.value,
																								)
																							}
																							placeholder="Chave"
																							className="w-16 rounded-lg border border-stone-200 bg-white px-2 py-2 text-sm"
																						/>
																						<input
																							type="text"
																							value={
																								opt.label
																							}
																							onChange={(
																								e,
																							) =>
																								updateEditOption(
																									idx,
																									'label',
																									e
																										.target
																										.value,
																								)
																							}
																							placeholder="Texto"
																							className="min-w-[180px] flex-1 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm"
																						/>
																						<OptionScoreInput
																							value={
																								opt.score
																							}
																							onChange={(
																								value,
																							) =>
																								updateEditOption(
																									idx,
																									'score',
																									value,
																								)
																							}
																						/>
																						<LucideIconPopover
																							value={
																								opt.icon_identifier ??
																								''
																							}
																							onChange={(
																								v,
																							) =>
																								updateEditOption(
																									idx,
																									'icon_identifier',
																									v,
																								)
																							}
																						/>
																						<ColorPopover
																							value={
																								colorValue
																							}
																							onChange={(
																								v,
																							) =>
																								updateEditOption(
																									idx,
																									'color',
																									v,
																								)
																							}
																						/>
																						<button
																							type="button"
																							onClick={() =>
																								removeEditOption(
																									idx,
																								)
																							}
																							className="rounded-full p-2 text-stone-400 hover:text-rose-600"
																						>
																							<Trash2
																								size={
																									14
																								}
																							/>
																						</button>
																					</div>
																				);
																			},
																		)}
																	</div>
																)}
																<div className="flex gap-2 pt-2">
																	<Button
																		text="Salvar"
																		className="rounded-full bg-black px-4 py-2 text-xs font-bold"
																		onClick={
																			handlePatchQuestion
																		}
																		disabled={
																			!editTitle.trim() ||
																			((editType ===
																				'SINGLE_CHOICE' ||
																				editType ===
																					'MULTIPLE_CHOICE') &&
																				editOptions.every(
																					(
																						o,
																					) =>
																						!o.key.trim() ||
																						!o.label.trim(),
																				))
																		}
																	/>
																	<button
																		type="button"
																		onClick={
																			cancelEditing
																		}
																		className="text-sm font-bold text-stone-400 hover:text-stone-600"
																	>
																		Cancelar
																	</button>
																</div>
															</div>
														) : (
															<div className="relative p-4">
																<div className="min-w-0 space-y-2">
																	<div className="flex items-center gap-3 pr-14">
																		<span
																			className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[13px] font-bold ${questionTextColorClass}`}
																			style={{
																				backgroundColor:
																					resolvedQuestionHex,
																			}}
																		>
																			{
																				q.number
																			}
																		</span>
																		<div className="flex min-w-0 flex-1 items-center gap-2">
																			{q.icon_identifier && (
																				<DynamicIcon
																					name={
																						q.icon_identifier as IconName
																					}
																					size={
																						18
																					}
																					className="shrink-0 text-stone-500"
																				/>
																			)}
																			<p className="min-w-0 truncate text-sm font-bold text-stone-900">
																				{
																					q.title
																				}
																			</p>
																		</div>
																	</div>
																	<div className="flex flex-wrap items-center gap-2">
																		{q.code ? (
																			<span
																				className="inline-flex items-center gap-1 rounded-full bg-brand-pink/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-brand-pink"
																				title="Esta pergunta alimenta o cálculo do match. Cada alternativa tem um significado fixo pela letra (A, B, C...): pode reescrever títulos, mas não troque as letras de lugar."
																			>
																				Match:{' '}
																				{MATCH_QUESTION_CODE_LABELS[
																					q
																						.code
																				] ??
																					q.code}
																			</span>
																		) : null}
																		<span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold text-stone-500">
																			{questionTypeIcon(
																				q.type,
																			)}
																			{questionTypeLabel(
																				q.type,
																			)}
																		</span>
																		{questionCategory && (
																			<span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold text-stone-500">
																				Categoria:{' '}
																				{
																					questionCategory.name
																				}
																			</span>
																		)}
																		{questionSubcategory && (
																			<span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold text-stone-500">
																				Subcategoria:{' '}
																				{
																					questionSubcategory.name
																				}
																			</span>
																		)}
																	</div>
																	{/* Preview da questão como o usuário vê */}
																	<div className="mt-2 space-y-2 rounded-xl p-3">
																		{(q.type ===
																			'SINGLE_CHOICE' ||
																			q.type ===
																				'MULTIPLE_CHOICE') &&
																			Array.isArray(
																				q.options,
																			) &&
																			q
																				.options
																				.length >
																				0 && (
																				<div className="space-y-2">
																					{q.options.map(
																						(
																							opt,
																						) => {
																							const rawOptionColor =
																								(
																									opt.color ??
																									''
																								).trim() ||
																								'#e5e7eb';
																							const resolvedOptHex =
																								resolveColorInputToHex(
																									rawOptionColor,
																								) ??
																								'#e5e7eb';
																							const textColorClass =
																								getContrastTextClassForHex(
																									resolvedOptHex,
																								);

																							return (
																								<div
																									key={
																										opt.key
																									}
																									className="flex items-stretch gap-2"
																								>
																									<button
																										type="button"
																										disabled
																										className="flex h-11 min-w-0 flex-1 items-center gap-3 rounded-lg border border-zinc-100 bg-white px-3 text-left text-xs text-zinc-700 shadow-sm"
																									>
																										<span
																											className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-bold ${textColorClass}`}
																											style={{
																												backgroundColor:
																													resolvedOptHex,
																											}}
																										>
																											{opt.icon_identifier ? (
																												<DynamicIcon
																													name={
																														opt.icon_identifier as IconName
																													}
																													size={
																														14
																													}
																												/>
																											) : (
																												opt.key
																											)}
																										</span>
																										<span className="min-w-0 flex-1 truncate">
																											{
																												opt.label
																											}
																										</span>
																									</button>
																									<div className="flex h-11 w-28 shrink-0 items-center justify-start rounded-lg border border-zinc-100 bg-white px-3 text-zinc-700 shadow-sm">
																										<span className="text-[13px] font-black tracking-wide text-stone-500">
																											Score:{' '}
																											<span className="text-[13px] font-bold text-stone-700">
																												{typeof opt.score ===
																												'number'
																													? opt.score
																													: '—'}
																											</span>
																										</span>
																									</div>
																								</div>
																							);
																						},
																					)}
																				</div>
																			)}
																	</div>
																</div>
																<div className="absolute right-4 top-4 flex gap-1">
																	<button
																		type="button"
																		onClick={() =>
																			moveQuestion(
																				q.id,
																				-1,
																			)
																		}
																		disabled={
																			isReordering ||
																			q.number ===
																				form
																					.form_question[0]
																					?.number
																		}
																		className="rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900 disabled:cursor-not-allowed disabled:opacity-30"
																		title="Mover para cima"
																	>
																		<ChevronUp
																			size={
																				16
																			}
																		/>
																	</button>
																	<button
																		type="button"
																		onClick={() =>
																			moveQuestion(
																				q.id,
																				1,
																			)
																		}
																		disabled={
																			isReordering ||
																			q.number ===
																				form
																					.form_question[
																					form
																						.form_question
																						.length -
																						1
																				]
																					?.number
																		}
																		className="rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900 disabled:cursor-not-allowed disabled:opacity-30"
																		title="Mover para baixo"
																	>
																		<ChevronDown
																			size={
																				16
																			}
																		/>
																	</button>
																	<button
																		type="button"
																		onClick={() =>
																			startEditing(
																				q,
																			)
																		}
																		className="rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900"
																		title="Editar"
																	>
																		<Pencil
																			size={
																				16
																			}
																		/>
																	</button>
																	<button
																		type="button"
																		onClick={() =>
																			deleteQuestion(
																				{
																					formId: id,
																					questionId:
																						q.id,
																				},
																			)
																		}
																		className="rounded-full p-2 text-stone-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
																		title="Excluir"
																	>
																		<Trash2
																			size={
																				16
																			}
																		/>
																	</button>
																</div>
															</div>
														)}
													</div>
												);
											})}
											{form.form_question.length ===
												0 && (
												<p className="rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 py-8 text-center text-sm text-stone-400">
													Nenhuma pergunta cadastrada.
													Use o bloco acima para criar
													a primeira.
												</p>
											)}
										</div>
									</div>
								</div>
							),
						},
						{
							id: 'respostas',
							label: 'Respostas',
							children: (
								<FormAnswersTab formId={id} form={form} />
							),
						},
						...(hasVinculosTab
							? [
									{
										id: 'vinculos' as const,
										label: 'Vínculos',
										children: (
											<FormCompaniesTab
												formId={id}
												form={form}
											/>
										),
									},
							  ]
							: []),
					]}
				/>
			)}
		</section>
	);
};

export { EditForm };
